import React, { useRef, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Button, Container, Grid, IconButton, Typography } from "@mui/material";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import LaptopIcon from "@mui/icons-material/Laptop";
import SchoolIcon from "@mui/icons-material/School";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import DevicesIcon from "@mui/icons-material/Devices";
import PeopleIcon from "@mui/icons-material/People";
import StarIcon from "@mui/icons-material/Star";
import BusinessIcon from "@mui/icons-material/Business";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import DescriptionIcon from "@mui/icons-material/Description";
import BoltIcon from "@mui/icons-material/Bolt";
import StaticPageLayout from "../../components/StaticPageLayout";
import * as styles from "./styles";

type TranslateFnT = (key: string, options?: Record<string, unknown>) => string;

type InViewResultT = {
  ref: React.RefObject<HTMLDivElement>;
  inView: boolean;
};

const useInView = (threshold = 0.15): InViewResultT => {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setInView(true);
      },
      { threshold },
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
};

type WhyCardT = {
  icon: React.ReactNode;
  title: string;
  description: string;
};

type StepT = {
  number: string;
  title: string;
  description: string;
};

type TestimonialT = {
  name: string;
  role: string;
  location: string;
  headline: string;
  quote: string;
  initials: string;
  avatarColor: string;
  thumbnail: string;
};

type StatItemT = {
  target: number;
  prefix?: string;
  suffix?: string;
  label: string;
  delay: string;
};

type EnergyPanelT = {
  stat: string;
  statFontSize: object;
  statGradient: string;
  statLabel: string;
  statSubLabel: string;
  headline: string;
  body: string;
  statLeft: boolean;
};

type BenefitCardT = {
  icon: React.ReactNode;
  title: string;
  body: string;
};

type AiCardT = {
  icon: React.ReactNode;
  title: string;
  body: string;
};

type TechnologyFeatureT = {
  title: string;
  description: string;
};

type HeroSectionPropsT = {
  t: TranslateFnT;
};

const HeroSection = ({ t }: HeroSectionPropsT) => {
  const { user } = useParams<{ user?: string }>();
  const prefix = user ? `/${user}` : "";

  const handleScrollDown = () => {
    const el = document.getElementById("stats-section");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <Box sx={styles.heroOuter}>
      <Box
        component="iframe"
        src="https://player.vimeo.com/video/1183883412?h=b6612cfc14&badge=0&autopause=0&background=1&autoplay=1&loop=1&muted=1&byline=0&title=0&controls=0"
        allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
        frameBorder="0"
        referrerPolicy="strict-origin-when-cross-origin"
        title={t("hero.iframeTitle")}
        sx={styles.heroVideoIframe}
      />
      <Box sx={styles.heroOverlay} />
      <Box sx={styles.heroVignette} />
      <Box sx={styles.heroTopGradient} />
      <Box sx={styles.heroBottomGradient} />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>
        <Box sx={{ textAlign: "center", mx: "auto" }}>
          <Typography sx={styles.heroEyebrow}>{t("hero.eyebrow")}</Typography>

          <Typography variant="h1" sx={styles.heroTitleLine1}>
            {t("hero.titleLine1")}
          </Typography>
          <Typography variant="h1" sx={styles.heroTitleLine2}>
            <Box component="span" sx={styles.gradientText}>
              {t("hero.titleLine2Gradient")}
            </Box>{" "}
            {t("hero.titleLine2Base")}
          </Typography>

          <Typography sx={styles.heroDescription}>{t("hero.description")}</Typography>

          <Typography sx={styles.heroTagline}>{t("hero.tagline")}</Typography>

          <Box
            sx={{
              opacity: 0,
              animation: "fadeUp 0.7s ease forwards",
              animationDelay: "0.85s",
            }}
          >
            <Button
              component={Link}
              to={`${prefix}/signup`}
              variant="contained"
              size="large"
              color="primary"
              sx={styles.heroCtaButton}
            >
              {t("hero.ctaButton")}
            </Button>
          </Box>
        </Box>
      </Container>

      <Box onClick={handleScrollDown} sx={styles.scrollIndicator}>
        <Typography sx={styles.scrollLabel}>{t("hero.scrollLabel")}</Typography>
        <KeyboardArrowDownIcon sx={{ fontSize: "1.4rem", color: "rgba(255,255,255,0.5)" }} />
      </Box>
    </Box>
  );
};

const useCountUp = (target: number, inView: boolean, duration = 1200) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let startTime: number | null = null;
    const animate = (ts: number) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      const eased = 1 - (1 - progress) ** 3;
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [inView, target, duration]);
  return count;
};

const StatCard = ({ item }: { item: StatItemT }) => {
  const { ref, inView } = useInView(0.3);
  const count = useCountUp(item.target, inView);
  return (
    <Box
      ref={ref}
      sx={{
        ...styles.statCardBase,
        opacity: 0,
        animation: inView ? "cardEntrance 0.65s cubic-bezier(0.22, 1, 0.36, 1) forwards" : "none",
        animationDelay: item.delay,
      }}
    >
      <Box sx={styles.statCardTopBar} />
      <Box sx={styles.statCardGlow} />
      <Typography sx={styles.statCardValue}>
        {item.prefix ?? ""}
        {count.toLocaleString("en-US")}
        {item.suffix ?? ""}
      </Typography>
      <Typography sx={styles.statCardLabel}>{item.label}</Typography>
    </Box>
  );
};

type StatsSectionPropsT = {
  statItems: StatItemT[];
};

const StatsSection = ({ statItems }: StatsSectionPropsT) => (
  <Box id="stats-section" sx={styles.statsSection}>
    <Container maxWidth="xl">
      <Box sx={styles.statsContainer}>
        {statItems.map((item) => (
          <StatCard key={item.label} item={item} />
        ))}
      </Box>
    </Container>
  </Box>
);

type WhyCardPropsT = {
  card: WhyCardT;
  delay: string;
};

const WhyCard = ({ card, delay }: WhyCardPropsT) => {
  const { ref, inView } = useInView();
  return (
    <Box
      ref={ref}
      sx={{
        ...styles.glassmorphismCard,
        p: 4,
        height: "100%",
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(32px)",
        transition: `opacity 0.7s ease ${delay}, transform 0.7s ease ${delay}`,
      }}
    >
      <Box sx={{ mb: 2 }}>{card.icon}</Box>
      <Typography variant="h6" sx={styles.whyCardTitle}>
        {card.title}
      </Typography>
      <Typography sx={styles.whyCardDescription}>{card.description}</Typography>
    </Box>
  );
};

type WhyJoinSectionPropsT = {
  whyCards: WhyCardT[];
  t: TranslateFnT;
};

const WhyJoinSection = ({ whyCards, t }: WhyJoinSectionPropsT) => {
  const { ref, inView } = useInView();
  return (
    <Box id="why-join" sx={styles.whyJoinOuter}>
      <Box sx={styles.whyJoinOverlay} />
      <Box sx={styles.whyJoinVignette} />
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Box
          ref={ref}
          sx={{
            textAlign: "center",
            mb: 8,
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(32px)",
            transition: "opacity 0.7s ease, transform 0.7s ease",
          }}
        >
          <Typography sx={styles.eyebrowSx}>{t("whyJoin.eyebrow")}</Typography>
          <Typography variant="h2" sx={styles.whyJoinSectionTitle}>
            {t("whyJoin.titleBase")}{" "}
            <Box
              component="span"
              sx={{
                background: "linear-gradient(90deg, #3b82f6 0%, #38bdf8 55%, #67e8f9 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              {t("whyJoin.titleGradient")}
            </Box>
            {t("whyJoin.titleSuffix")}
          </Typography>
          <Typography sx={styles.whyJoinSectionSubtitle}>{t("whyJoin.subtitle")}</Typography>
        </Box>
      </Container>
      <Box sx={{ px: { xs: 2, md: 4 }, position: "relative", zIndex: 1 }}>
        <Grid container spacing={3}>
          {whyCards.map((card, i) => (
            <Grid key={card.title} item xs={12} sm={6} lg>
              <WhyCard card={card} delay={`${i * 0.12}s`} />
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

type ScaleYourImpactSectionPropsT = {
  aiCards: AiCardT[];
  t: TranslateFnT;
};

const ScaleYourImpactSection = ({ aiCards, t }: ScaleYourImpactSectionPropsT) => {
  const { ref, inView } = useInView();
  return (
    <Box sx={styles.scaleImpactOuter}>
      <Box sx={styles.scaleImpactGlow} />
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Box
          ref={ref}
          sx={{
            textAlign: "center",
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(32px)",
            transition: "opacity 0.8s ease, transform 0.8s ease",
          }}
        >
          <Typography sx={styles.eyebrowSx}>{t("scaleImpact.eyebrow")}</Typography>
          <Typography variant="h2" sx={styles.scaleImpactTitle}>
            {t("scaleImpact.titleBase")}{" "}
            <Box
              component="span"
              sx={{
                background: "linear-gradient(90deg, #38bdf8 0%, #67e8f9 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              {t("scaleImpact.titleGradient")}
            </Box>
          </Typography>
          <Typography sx={styles.scaleImpactSubtitle}>{t("scaleImpact.subtitle")}</Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 3,
            }}
          >
            {aiCards.map((card, i) => (
              <Box
                key={card.title}
                sx={{
                  ...styles.aiCardOuter,
                  opacity: inView ? 1 : 0,
                  transform: inView ? "translateY(0)" : "translateY(24px)",
                  transition: `opacity 0.7s ease ${0.15 + i * 0.1}s, transform 0.7s ease ${0.15 + i * 0.1}s`,
                }}
              >
                <Box sx={styles.aiCardGlow} />
                <Box sx={styles.aiCardIconBox}>{card.icon}</Box>
                <Typography sx={styles.aiCardTitle}>{card.title}</Typography>
                <Typography sx={styles.aiCardBody}>{card.body}</Typography>
              </Box>
            ))}
          </Box>
          <Typography
            sx={{
              fontFamily: "'Barlow', sans-serif",
              fontSize: { xs: "1.1rem", md: "1.3rem" },
              color: "rgba(255,255,255,0.6)",
              textAlign: "center",
              mt: { xs: 6, md: 8 },
              mx: "auto",
              whiteSpace: { xs: "normal", md: "nowrap" },
            }}
          >
            {t("scaleImpact.paigeLine1")}{" "}
            <Box component="span" sx={{ color: "#38bdf8", fontWeight: 600 }}>
              {t("scaleImpact.paigeName")}
            </Box>{" "}
            {t("scaleImpact.paigeLine2")}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

type StepItemPropsT = {
  step: StepT;
  index: number;
};

const StepItem = ({ step, index }: StepItemPropsT) => {
  const { ref, inView } = useInView();
  return (
    <Box
      ref={ref}
      sx={{
        flex: 1,
        textAlign: "center",
        px: { xs: 2, md: 4 },
        position: "relative",
        zIndex: 1,
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(32px)",
        transition: `opacity 0.7s ease ${index * 0.15}s, transform 0.7s ease ${index * 0.15}s`,
      }}
    >
      <Typography sx={styles.stepNumber}>{step.number}</Typography>
      <Typography variant="h6" sx={styles.stepTitle}>
        {step.title}
      </Typography>
      <Typography sx={styles.stepDescription}>{step.description}</Typography>
    </Box>
  );
};

type HowItWorksSectionPropsT = {
  steps: StepT[];
  t: TranslateFnT;
};

const HowItWorksSection = ({ steps, t }: HowItWorksSectionPropsT) => {
  const { ref, inView } = useInView();
  return (
    <Box id="how-it-works" sx={styles.howItWorksOuter}>
      <Box sx={styles.howItWorksTopFade} />
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Box
          ref={ref}
          sx={{
            textAlign: "center",
            mb: 8,
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(32px)",
            transition: "opacity 0.7s ease, transform 0.7s ease",
          }}
        >
          <Typography sx={styles.eyebrowSx}>{t("howItWorks.eyebrow")}</Typography>
          <Typography variant="h2" sx={styles.howItWorksTitle}>
            {t("howItWorks.titleBase")}{" "}
            <Box component="span" sx={styles.gradientText}>
              {t("howItWorks.titleGradient")}
            </Box>
          </Typography>
        </Box>
        <Box sx={styles.howItWorksStepsContainer}>
          <Box sx={styles.howItWorksConnector} />
          {steps.map((step, i) => (
            <StepItem key={step.number} step={step} index={i} />
          ))}
        </Box>
      </Container>
    </Box>
  );
};

type TestimonialsSectionPropsT = {
  testimonials: TestimonialT[];
  t: TranslateFnT;
};

const TestimonialsSection = ({ testimonials, t }: TestimonialsSectionPropsT) => {
  const [active, setActive] = useState(0);
  const { ref, inView } = useInView();

  const goTo = (index: number) => {
    if (index === active) return;
    setActive(index);
  };

  const prev = () => goTo((active - 1 + testimonials.length) % testimonials.length);
  const next = () => goTo((active + 1) % testimonials.length);
  const dotKeys = testimonials.map((item) => `dot-${item.name}`);

  return (
    <Box
      sx={{ py: { xs: 10, md: 16 }, background: "linear-gradient(to bottom, #0a0f1e, #050c23)", overflow: "hidden" }}
    >
      <Container maxWidth="lg">
        <Box
          ref={ref}
          sx={{
            textAlign: "center",
            mb: { xs: 6, md: 10 },
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(32px)",
            transition: "opacity 0.7s ease, transform 0.7s ease",
          }}
        >
          <Typography sx={styles.eyebrowSx}>{t("testimonials.eyebrow")}</Typography>
          <Typography variant="h2" sx={styles.testimonialsSectionTitle}>
            {t("testimonials.titleBase")}{" "}
            <Box
              component="span"
              sx={{
                background: "linear-gradient(90deg, #3b82f6 0%, #38bdf8 55%, #67e8f9 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              {t("testimonials.titleGradient")}
            </Box>
          </Typography>
          <Typography sx={styles.testimonialsSectionSubtitle}>{t("testimonials.subtitle")}</Typography>
        </Box>
      </Container>

      <Box sx={{ position: "relative" }}>
        <IconButton onClick={prev} sx={{ ...styles.testimonialNavButton, left: { xs: 8, md: 16, lg: 24 } }}>
          <KeyboardArrowDownIcon sx={{ transform: "rotate(90deg)" }} />
        </IconButton>

        <Box sx={{ overflow: "hidden", px: { xs: 2, md: 8 } }}>
          <Box
            sx={{
              ...styles.testimonialTrack,
              transform: `translateX(calc(-${active} * (min(600px, 80vw) + 24px) + 50% - min(300px, 40vw)))`,
            }}
          >
            {testimonials.map((item, index) => {
              const testimonialOpacity = (() => {
                if (index === active) return 1;
                return Math.abs(index - active) === 1 ? 0.4 : 0;
              })();
              return (
                <Box
                  key={item.name}
                  sx={{
                    flex: "0 0 min(600px, 80vw)",
                    width: "min(600px, 80vw)",
                    opacity: testimonialOpacity,
                    transform: index === active ? "scale(1)" : "scale(0.72)",
                    transition: "opacity 0.4s cubic-bezier(0.22,1,0.36,1), transform 0.4s cubic-bezier(0.22,1,0.36,1)",
                    pointerEvents: index === active ? "auto" : "none",
                  }}
                >
                  <Box sx={styles.testimonialCardOuter}>
                    <Box sx={styles.testimonialThumbnailContainer}>
                      <Box component="img" src={item.thumbnail} alt={item.name} sx={styles.testimonialThumbnailImage} />
                    </Box>

                    <Box sx={{ p: { xs: 3, md: 4 } }}>
                      <Box sx={{ mb: 3 }}>
                        <Typography sx={styles.testimonialHeadline}>&ldquo;{item.headline}&rdquo;</Typography>
                        <Typography sx={styles.testimonialQuote}>{item.quote}</Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box sx={{ ...styles.testimonialAvatarCircle, bgcolor: item.avatarColor }}>
                          <Typography sx={{ color: "white", fontWeight: 700, fontSize: "0.8rem" }}>
                            {item.initials}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography sx={styles.testimonialName}>{item.name}</Typography>
                          <Typography sx={styles.testimonialRole}>
                            {item.role}
                            {item.location ? ` · ${item.location}` : ""}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        <IconButton onClick={next} sx={{ ...styles.testimonialNavButton, right: { xs: 8, md: 16, lg: 24 } }}>
          <KeyboardArrowDownIcon sx={{ transform: "rotate(-90deg)" }} />
        </IconButton>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 5 }}>
        {dotKeys.map((key, dotIdx) => (
          <Box
            key={key}
            onClick={() => goTo(dotIdx)}
            sx={{
              width: dotIdx === active ? 24 : 8,
              height: 8,
              borderRadius: "4px",
              bgcolor: dotIdx === active ? "#38bdf8" : "rgba(255,255,255,0.2)",
              cursor: "pointer",
              transition: "all 0.35s ease",
              "&:hover": { bgcolor: dotIdx === active ? "#38bdf8" : "rgba(255,255,255,0.4)" },
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

type FinalCTASectionPropsT = {
  t: TranslateFnT;
};

const FinalCTASection = ({ t }: FinalCTASectionPropsT) => {
  const { user } = useParams<{ user?: string }>();
  const prefix = user ? `/${user}` : "";
  const { ref, inView } = useInView();
  return (
    <Box sx={styles.finalCtaOuter}>
      <Box
        component="video"
        autoPlay
        muted
        loop
        playsInline
        src="/img/assets/Powur_SolarInstall (2).mp4"
        sx={styles.finalCtaVideo}
      />
      <Box sx={styles.finalCtaOverlay} />
      <Box sx={styles.finalCtaVignette} />
      <Container maxWidth={false} sx={{ position: "relative", zIndex: 2, px: { xs: 4, md: 10 } }}>
        <div
          ref={ref}
          style={{
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(32px)",
            transition: "opacity 0.8s ease, transform 0.8s ease",
          }}
        >
          <Typography sx={{ ...styles.eyebrowSx, mb: 3 }}>{t("finalCta.eyebrow")}</Typography>
          <Typography variant="h2" sx={styles.finalCtaTitle}>
            {t("finalCta.titleBase")}
            <br />
            <Box component="span" sx={styles.gradientText}>
              {t("finalCta.titleGradient")}
            </Box>
          </Typography>
          <Typography sx={styles.finalCtaSubtitle}>{t("finalCta.subtitle")}</Typography>
          <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
            <Button component={Link} to={`${prefix}/signup`} variant="contained" sx={styles.finalCtaSellerButton}>
              {t("finalCta.ctaButton")}
            </Button>
          </Box>
        </div>
      </Container>
    </Box>
  );
};

type EnergyPanelPropsT = {
  panel: EnergyPanelT;
  index: number;
};

const EnergyPanel = ({ panel, index }: EnergyPanelPropsT) => {
  const { ref, inView } = useInView(0.1);
  const statBlock = (
    <Box
      sx={{
        flex: 1,
        textAlign: "center",
        py: { xs: 3, md: 0 },
      }}
    >
      <Typography
        sx={{
          fontSize: panel.statFontSize,
          fontWeight: 800,
          fontFamily: "'Poppins', sans-serif",
          lineHeight: 1,
          background: panel.statGradient,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          backgroundClip: "text",
          mb: 1.5,
        }}
      >
        {panel.stat}
      </Typography>
      <Typography
        sx={{
          fontFamily: "'Barlow', sans-serif",
          fontWeight: 700,
          fontSize: { xs: "1rem", md: "1.1rem" },
          color: "rgba(255,255,255,0.82)",
          mb: 0.5,
        }}
      >
        {panel.statLabel}
      </Typography>
      <Typography
        sx={{
          fontFamily: "'Barlow', sans-serif",
          fontSize: "0.85rem",
          color: "rgba(255,255,255,0.5)",
        }}
      >
        {panel.statSubLabel}
      </Typography>
    </Box>
  );
  const copyBlock = (
    <Box
      sx={{
        flex: 1,
        py: { xs: 3, md: 0 },
        pl: { md: panel.statLeft ? 8 : 0 },
        pr: { md: panel.statLeft ? 0 : 8 },
      }}
    >
      <Typography
        variant="h3"
        sx={{
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 800,
          fontSize: { xs: "1.6rem", md: "2.2rem" },
          color: "white",
          lineHeight: 1.2,
          mb: 2.5,
        }}
      >
        {panel.headline}
      </Typography>
      <Typography
        sx={{
          fontFamily: "'Barlow', sans-serif",
          fontSize: { xs: "1rem", md: "1.1rem" },
          color: "rgba(255,255,255,0.82)",
          lineHeight: 1.75,
          maxWidth: 520,
        }}
      >
        {panel.body}
      </Typography>
    </Box>
  );
  return (
    <Box
      ref={ref}
      sx={{
        py: { xs: 6, md: 10 },
        borderTop: index === 0 ? "1px solid rgba(56,189,248,0.18)" : "none",
        borderBottom: "1px solid rgba(56,189,248,0.18)",
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(40px)",
        transition: `opacity 0.8s ease ${index * 0.1}s, transform 0.8s ease ${index * 0.1}s`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "center",
          gap: { xs: 4, md: 0 },
        }}
      >
        {panel.statLeft ? (
          <>
            {statBlock}
            {copyBlock}
          </>
        ) : (
          <>
            {copyBlock}
            {statBlock}
          </>
        )}
      </Box>
    </Box>
  );
};

const GLOWS = [
  { top: -60, left: -60, bottom: "auto" as const, right: "auto" as const },
  { top: -60, right: -60, bottom: "auto" as const, left: "auto" as const },
  { bottom: -60, right: -60, top: "auto" as const, left: "auto" as const },
  { bottom: -60, left: -60, top: "auto" as const, right: "auto" as const },
];

type BenefitCardPropsT = {
  card: BenefitCardT;
  index: number;
};

const BenefitCard = ({ card, index }: BenefitCardPropsT) => {
  const { ref, inView } = useInView(0.1);
  const glow = GLOWS[index % GLOWS.length];
  return (
    <Box
      ref={ref}
      sx={{
        background: "rgba(255,255,255,0.04)",
        border: "1px solid rgba(56,189,248,0.10)",
        borderRadius: "20px",
        backdropFilter: "blur(12px)",
        p: 3.5,
        position: "relative",
        overflow: "hidden",
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(32px)",
        transition: `opacity 0.7s ease ${(index % 3) * 0.1}s, transform 0.7s ease ${(index % 3) * 0.1}s`,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: glow.top,
          bottom: glow.bottom,
          left: glow.left,
          right: glow.right,
          width: 200,
          height: 200,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(56,189,248,0.10) 0%, transparent 70%)",
          pointerEvents: "none",
          filter: "blur(20px)",
        }}
      />
      <Box sx={{ mb: 2, position: "relative" }}>{card.icon}</Box>
      <Typography
        sx={{
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 700,
          fontSize: "1rem",
          color: "white",
          mb: 1.5,
          position: "relative",
        }}
      >
        {card.title}
      </Typography>
      <Typography
        sx={{
          fontFamily: "'Barlow', sans-serif",
          fontSize: "0.92rem",
          color: "rgba(255,255,255,0.82)",
          lineHeight: 1.65,
          position: "relative",
        }}
      >
        {card.body}
      </Typography>
    </Box>
  );
};

type WhyEnergyWhyPowurSectionPropsT = {
  energyPanels: EnergyPanelT[];
  benefitCards: BenefitCardT[];
  t: TranslateFnT;
};

const WhyEnergyWhyPowurSection = ({ energyPanels, benefitCards, t }: WhyEnergyWhyPowurSectionPropsT) => {
  const { ref: energyHeaderRef, inView: energyHeaderInView } = useInView(0.2);
  const { ref: powurHeaderRef, inView: powurHeaderInView } = useInView(0.2);
  const { ref: cardsRef, inView: cardsInView } = useInView(0.1);
  return (
    <>
      <Box
        sx={{
          background: "#040922",
          py: { xs: 10, md: 16 },
        }}
      >
        <Container maxWidth="lg">
          <Box
            ref={energyHeaderRef}
            sx={{
              textAlign: "center",
              mb: { xs: 6, md: 10 },
              opacity: energyHeaderInView ? 1 : 0,
              transform: energyHeaderInView ? "translateY(0)" : "translateY(32px)",
              transition: "opacity 0.8s ease, transform 0.8s ease",
            }}
          >
            <Typography sx={styles.eyebrowSx}>{t("whyEnergy.eyebrow")}</Typography>
            <Typography
              variant="h2"
              sx={{
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 800,
                fontSize: { xs: "2.2rem", md: "3.2rem", lg: "4rem" },
                color: "white",
                lineHeight: 1.15,
                letterSpacing: "-0.02em",
                mb: 3,
              }}
            >
              {t("whyEnergy.titleBase")}
              <br />
              {t("whyEnergy.titleMiddle")}
              <Box component="span" sx={styles.gradientText}>
                {t("whyEnergy.titleGradient")}
              </Box>
            </Typography>
            <Typography
              sx={{
                fontFamily: "'Barlow', sans-serif",
                fontSize: { xs: "1rem", md: "1.1rem" },
                color: "rgba(255,255,255,0.6)",
                maxWidth: 700,
                mx: "auto",
                whiteSpace: { xs: "normal", md: "nowrap" },
              }}
            >
              {t("whyEnergy.subtitle")}
            </Typography>
          </Box>
          {energyPanels.map((panel, i) => (
            <EnergyPanel key={panel.headline} panel={panel} index={i} />
          ))}
        </Container>
      </Box>

      <Box
        sx={{
          background: "#050c23",
          py: { xs: 10, md: 16 },
        }}
      >
        <Container maxWidth="lg">
          <Box
            ref={powurHeaderRef}
            sx={{
              textAlign: "center",
              mb: { xs: 6, md: 10 },
              opacity: powurHeaderInView ? 1 : 0,
              transform: powurHeaderInView ? "translateY(0)" : "translateY(32px)",
              transition: "opacity 0.8s ease, transform 0.8s ease",
            }}
          >
            <Typography sx={styles.eyebrowSx}>{t("whyPowur.eyebrow")}</Typography>
            <Typography
              variant="h2"
              sx={{
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 800,
                fontSize: { xs: "2.2rem", md: "3.2rem", lg: "4rem" },
                color: "white",
                lineHeight: 1.15,
                letterSpacing: "-0.02em",
                mb: 3,
              }}
            >
              {t("whyPowur.titleBase")}{" "}
              <Box component="span" sx={styles.gradientText}>
                {t("whyPowur.titleGradient")}
              </Box>
            </Typography>
            <Typography
              sx={{
                fontFamily: "'Barlow', sans-serif",
                fontSize: { xs: "1rem", md: "1.1rem" },
                color: "rgba(255,255,255,0.82)",
                lineHeight: 1.7,
                maxWidth: 800,
                mx: "auto",
                whiteSpace: { xs: "normal", md: "nowrap" },
              }}
            >
              {t("whyPowur.subtitle")}
            </Typography>
          </Box>
          <Box
            ref={cardsRef}
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "1fr 1fr 1fr" },
              gap: 2.5,
              opacity: cardsInView ? 1 : 0,
              transition: "opacity 0.6s ease",
            }}
          >
            {benefitCards.slice(0, 3).map((card, i) => (
              <BenefitCard key={card.title} card={card} index={i} />
            ))}

            <Box
              component="img"
              src="/img/assets/join-img-1.jpg"
              alt={t("whyPowur.gridImageAlt")}
              sx={{
                gridColumn: { xs: "span 1", sm: "span 2", lg: "span 2" },
                borderRadius: "20px",
                objectFit: "cover",
                width: "100%",
                height: { xs: 220, sm: 260 },
                display: "block",
                border: "1px solid rgba(56,189,248,0.10)",
              }}
            />

            <BenefitCard key={benefitCards[3].title} card={benefitCards[3]} index={3} />

            <BenefitCard key={benefitCards[4].title} card={benefitCards[4]} index={4} />

            <Box
              component="img"
              src="/img/assets/join-img-2.jpg"
              alt={t("whyPowur.gridImageAlt")}
              sx={{
                gridColumn: { xs: "span 1", sm: "span 2", lg: "span 2" },
                borderRadius: "20px",
                objectFit: "cover",
                width: "100%",
                height: { xs: 220, sm: 260 },
                display: "block",
                border: "1px solid rgba(56,189,248,0.10)",
              }}
            />

            {benefitCards.slice(5, 8).map((card, i) => (
              <BenefitCard key={card.title} card={card} index={i + 5} />
            ))}

            <Box
              component="img"
              src="/img/assets/join-img-3.jpg"
              alt={t("whyPowur.gridImageAlt")}
              sx={{
                gridColumn: { xs: "span 1", sm: "span 2", lg: "span 2" },
                borderRadius: "20px",
                objectFit: "cover",
                width: "100%",
                height: { xs: 220, sm: 280 },
                display: "block",
                border: "1px solid rgba(56,189,248,0.10)",
              }}
            />

            <BenefitCard key={benefitCards[8].title} card={benefitCards[8]} index={8} />
          </Box>
        </Container>
      </Box>
    </>
  );
};

type RewardSectionPropsT = {
  rewardStatements: string[];
  t: TranslateFnT;
};

const RewardSection = ({ rewardStatements, t }: RewardSectionPropsT) => {
  const { ref, inView } = useInView();
  return (
    <Box sx={styles.rewardOuter}>
      <Box sx={styles.rewardBottomGradient} />
      <Container maxWidth="xl" sx={{ px: { xs: 3, md: 8 }, position: "relative", zIndex: 1 }}>
        <div ref={ref}>
          <Grid container spacing={{ xs: 6, md: 10 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                sx={{
                  ...styles.rewardEyebrowAnimated,
                  animation: inView ? "copySlideIn 0.55s cubic-bezier(0.22, 1, 0.36, 1) forwards" : "none",
                  animationDelay: "0s",
                }}
              >
                {t("reward.eyebrow")}
              </Typography>
              <Typography
                variant="h1"
                sx={{
                  ...styles.rewardTitle,
                  animation: inView ? "copySlideIn 0.55s cubic-bezier(0.22, 1, 0.36, 1) forwards" : "none",
                  animationDelay: "0.1s",
                }}
              >
                <Box component="span" sx={{ color: "white" }}>
                  {t("reward.titleWhite")}{" "}
                </Box>
                <Box component="span" sx={styles.gradientText}>
                  {t("reward.titleGradient")}
                </Box>
              </Typography>
              <Typography
                sx={{
                  ...styles.rewardDescription,
                  animation: inView ? "copySlideIn 0.55s cubic-bezier(0.22, 1, 0.36, 1) forwards" : "none",
                  animationDelay: "0.2s",
                }}
              >
                {t("reward.description")}
              </Typography>
              <Box
                sx={{
                  ...styles.rewardStatementsContainer,
                  animation: inView ? "copySlideIn 0.55s cubic-bezier(0.22, 1, 0.36, 1) forwards" : "none",
                  animationDelay: "0.3s",
                }}
              >
                {rewardStatements.map((line) => (
                  <Box key={line} sx={{ display: "flex", alignItems: "center", gap: 2, pt: 2, pb: 2 }}>
                    <Box sx={styles.rewardCheckIcon}>
                      <CheckIcon sx={{ fontSize: 13, color: "#38bdf8" }} />
                    </Box>
                    <Typography sx={styles.rewardStatementText}>{line}</Typography>
                  </Box>
                ))}
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  ...styles.rewardCardContainer,
                  animation: inView ? "copySlideIn 0.55s cubic-bezier(0.22, 1, 0.36, 1) forwards" : "none",
                  animationDelay: "0.25s",
                }}
              >
                <Box sx={styles.rewardCardGlow} />
                <Box sx={styles.rewardCard}>
                  <Box sx={styles.rewardCardTopBar} />
                  <Box sx={styles.rewardCardGlowInner} />
                  <Typography sx={{ ...styles.eyebrowSx, mb: 2, position: "relative" }}>
                    {t("reward.cardEyebrow")}
                  </Typography>
                  <Typography sx={styles.rewardCardValue}>{t("reward.cardValue")}</Typography>
                  <Typography sx={styles.rewardCardLabel}>{t("reward.cardLabel")}</Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </div>
      </Container>
    </Box>
  );
};

const provenAtScaleBadges = [
  "/img/assets/Inc5000_963.png",
  "/img/assets/Inc5000_61.png",
  "/img/assets/Inc5000_43.png",
  "/img/assets/Inc5000_130.png",
  "/img/assets/Credibility - bbb.png",
  "/img/assets/Credibility - Bcorp.png",
];

type ProvenAtScaleSectionPropsT = {
  t: TranslateFnT;
};

const ProvenAtScaleSection = ({ t }: ProvenAtScaleSectionPropsT) => {
  const { user } = useParams<{ user?: string }>();
  const prefix = user ? `/${user}` : "";
  const { ref, inView } = useInView();
  return (
    <Box sx={styles.provenAtScaleOuter}>
      <Box sx={styles.provenAtScaleRightOverlay} />
      <Box sx={styles.provenAtScaleTopFade} />
      <Box sx={styles.provenAtScaleBottomFade} />

      <Container maxWidth="xl" sx={{ px: { xs: 3, md: 8 }, py: { xs: 10, md: 16 }, position: "relative", zIndex: 3 }}>
        <Grid container>
          <Grid item xs={12} md={6} />
          <Grid item xs={12} md={6}>
            <Box
              ref={ref}
              sx={{
                ...styles.provenAtScaleContentAnimated,
                animation: inView ? "copySlideIn 0.55s cubic-bezier(0.22, 1, 0.36, 1) forwards" : "none",
                animationDelay: "0.1s",
              }}
            >
              <Typography sx={styles.eyebrowSx}>{t("provenAtScale.eyebrow")}</Typography>
              <Typography variant="h2" sx={styles.provenAtScaleTitle}>
                <Box
                  component="span"
                  sx={{
                    background: "linear-gradient(90deg, #3b82f6 0%, #38bdf8 55%, #67e8f9 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  {t("provenAtScale.titleGradient")}
                </Box>
                {t("provenAtScale.titleBase")}
                <br />
                {t("provenAtScale.titleLine2")}
                <br />
                {t("provenAtScale.titleLine3")}
              </Typography>
              <Typography sx={styles.provenAtScaleDescription}>{t("provenAtScale.description")}</Typography>
              <Button component={Link} to={`${prefix}/signup`} variant="contained" sx={styles.provenAtScaleJoinButton}>
                {t("provenAtScale.ctaButton")}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Container>

      <Box sx={styles.provenAtScaleBadgesContainer}>
        <Box sx={{ ...styles.provenAtScaleBadgesInnerMobile, display: { xs: "grid", md: "none" } }}>
          {provenAtScaleBadges.map((src) => (
            <Box key={src} component="img" src={src} alt="" sx={styles.provenAtScaleBadgeImage} />
          ))}
        </Box>
        <Box sx={{ ...styles.provenAtScaleBadgesInner, display: { xs: "none", md: "flex" } }}>
          {provenAtScaleBadges.map((src) => (
            <Box key={src} component="img" src={src} alt="" sx={styles.provenAtScaleBadgeImage} />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

type SmartPlatformSectionPropsT = {
  t: TranslateFnT;
};

const SmartPlatformSection = ({ t }: SmartPlatformSectionPropsT) => {
  const { ref, inView } = useInView();
  useEffect(() => {
    if (!document.querySelector("script[src='https://player.vimeo.com/api/player.js']")) {
      const script = document.createElement("script");
      script.src = "https://player.vimeo.com/api/player.js";
      document.body.appendChild(script);
    }
  }, []);
  return (
    <Box sx={styles.smartPlatformOuter}>
      <Box sx={styles.smartPlatformTopFade} />
      <Container maxWidth="xl" sx={{ px: { xs: 3, md: 8 }, position: "relative", zIndex: 1 }}>
        <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center" sx={{ mb: { xs: 8, md: 12 } }}>
          <Grid item xs={12} md={7}>
            <Box
              ref={ref}
              sx={{
                ...styles.smartPlatformContentAnimated,
                animation: inView ? "copySlideIn 0.55s cubic-bezier(0.22, 1, 0.36, 1) forwards" : "none",
                animationDelay: "0.1s",
              }}
            >
              <Typography sx={styles.eyebrowSx}>{t("smartPlatform.eyebrow")}</Typography>
              <Typography variant="h2" sx={styles.smartPlatformTitle}>
                <Box
                  component="span"
                  sx={{
                    background: "linear-gradient(90deg, #3b82f6 0%, #38bdf8 55%, #67e8f9 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  {t("smartPlatform.titleGradient")}
                </Box>
                {t("smartPlatform.titleBase")}
                <br />
                {t("smartPlatform.titleLine2")}
              </Typography>
              <Typography sx={styles.smartPlatformDescription}>{t("smartPlatform.description")}</Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 4 }}>
                {[
                  t("smartPlatform.checkItems.aiProposals"),
                  t("smartPlatform.checkItems.digitalContracts"),
                  t("smartPlatform.checkItems.liveTracking"),
                  t("smartPlatform.checkItems.commissionReporting"),
                ].map((item) => (
                  <Box key={item} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <CheckCircleOutlineIcon sx={{ color: "#38bdf8", fontSize: "1.2rem", flexShrink: 0 }} />
                    <Typography sx={styles.smartPlatformCheckItem}>{item}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={5}>
            <Box sx={{ position: "relative", borderRadius: "20px", overflow: "hidden" }}>
              <Box sx={{ padding: "75% 0 0 0", position: "relative" }}>
                <Box
                  component="iframe"
                  src="https://player.vimeo.com/video/1177825874?h=5f5a12226f&badge=0&autopause=0&player_id=0&app_id=58479&autoplay=1&muted=1"
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  title={t("smartPlatform.videoIframeTitle")}
                  sx={styles.smartPlatformVideoIframe}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

const PROPOSAL_IMAGES = [
  "/img/assets/Proposal-1.png",
  "/img/assets/Proposal-2.png",
  "/img/assets/Proposal-3.png",
  "/img/assets/Proposal-4.png",
];

type TechnologySectionPropsT = {
  technologyFeatures: TechnologyFeatureT[];
  t: TranslateFnT;
};

const TechnologySection = ({ technologyFeatures, t }: TechnologySectionPropsT) => {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActive((p) => (p + 1) % PROPOSAL_IMAGES.length);
    }, 3500);
    return () => clearInterval(timer);
  }, [active]);

  const prev = () => setActive((a) => (a - 1 + PROPOSAL_IMAGES.length) % PROPOSAL_IMAGES.length);
  const next = () => setActive((a) => (a + 1) % PROPOSAL_IMAGES.length);

  return (
    <Box sx={{ background: "#0a0f1e", py: { xs: 10, md: 16 } }}>
      <Container maxWidth="xl" sx={{ px: { xs: 3, md: 8 } }}>
        <Box sx={styles.technologyCarouselRow}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={styles.technologyEyebrow}>{t("technology.eyebrow")}</Typography>
            <Typography variant="h2" sx={styles.technologyTitle}>
              <Box component="span" sx={{ color: "white", whiteSpace: "nowrap" }}>
                {t("technology.titleWhite1")}
              </Box>
              <br />
              <Box component="span" sx={{ color: "white", whiteSpace: "nowrap" }}>
                {t("technology.titleWhite2")}{" "}
              </Box>
              <Box component="span" sx={{ ...styles.gradientText, whiteSpace: "nowrap" }}>
                {t("technology.titleGradient")}
              </Box>
            </Typography>
            <Typography sx={styles.technologyDescription}>{t("technology.description")}</Typography>
            <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none", mb: 4 }}>
              {technologyFeatures.map((feature) => (
                <Box
                  key={feature.title}
                  component="li"
                  sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 2.5 }}
                >
                  <Box sx={{ ...styles.rewardCheckIcon, mt: "2px" }}>
                    <CheckIcon sx={{ fontSize: 13, color: "#38bdf8" }} />
                  </Box>
                  <Box>
                    <Typography sx={styles.technologyFeatureTitle}>{feature.title}</Typography>
                    <Typography sx={styles.technologyFeatureDescription}>{feature.description}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Box sx={styles.technologyCarouselControls}>
              <IconButton onClick={prev} sx={styles.technologyCarouselArrow}>
                <ArrowBackIosNewIcon sx={{ fontSize: "0.9rem" }} />
              </IconButton>
              <IconButton onClick={next} sx={styles.technologyCarouselArrow}>
                <ArrowForwardIosIcon sx={{ fontSize: "0.9rem" }} />
              </IconButton>
            </Box>

            <Box sx={{ position: "relative" }}>
              {PROPOSAL_IMAGES.map((src, i) => (
                <Box
                  key={src}
                  sx={{
                    position: i === 0 ? "relative" : "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    opacity: i === active ? 1 : 0,
                    transition: "opacity 0.5s ease",
                    pointerEvents: i === active ? "auto" : "none",
                  }}
                >
                  <Box component="img" src={src} alt="" sx={styles.technologyCarouselImage} />
                </Box>
              ))}
            </Box>

            <Box sx={styles.technologyCarouselDots}>
              {PROPOSAL_IMAGES.map((src, i) => (
                <Box
                  key={src}
                  onClick={() => setActive(i)}
                  sx={{
                    ...styles.technologyCarouselDot,
                    background: i === active ? "#38bdf8" : "rgba(255,255,255,0.25)",
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

type InvitationSectionPropsT = {
  t: TranslateFnT;
};

const InvitationSection = ({ t }: InvitationSectionPropsT) => {
  const { user } = useParams<{ user?: string }>();
  const prefix = user ? `/${user}` : "";
  const { ref, inView } = useInView(0.1);

  const pricingFeatures = [
    t("invitation.features.fullAccess"),
    t("invitation.features.proposalTools"),
    t("invitation.features.training"),
    t("invitation.features.commissionTracking"),
    t("invitation.features.cashAdvance"),
    t("invitation.features.stockOptions"),
    t("invitation.features.teamBuilding"),
  ];

  return (
    <Box sx={styles.invitationOuter}>
      <Box sx={styles.invitationTopFade} />
      <Box sx={styles.invitationBottomFade} />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>
        <Box
          ref={ref}
          sx={{
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(40px)",
            transition: "opacity 0.9s ease, transform 0.9s ease",
            textAlign: "center",
          }}
        >
          <Typography sx={styles.invitationEyebrow}>
            <Box component="span" sx={{ color: "#38bdf8" }}>
              {t("invitation.eyebrow1")}
            </Box>{" "}
            <Box component="span" sx={{ color: "#38bdf8" }}>
              {t("invitation.eyebrow2")}
            </Box>
          </Typography>

          <Typography sx={styles.invitationTitle}>
            {t("invitation.titleBase")}{" "}
            <Box component="span" sx={{ ...styles.gradientText }}>
              {t("invitation.titleGradient1")}
            </Box>
            <br />
            {t("invitation.titleLine2")}{" "}
            <Box component="span" sx={{ ...styles.gradientText }}>
              {t("invitation.titleGradient2")}
            </Box>
          </Typography>

          <Typography sx={styles.invitationSubtitle}>{t("invitation.subtitle")}</Typography>

          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Box sx={styles.pricingCard}>
              <Box sx={styles.pricingCardGlowTopLeft} />
              <Box sx={styles.pricingCardGlowBottomRight} />

              <Box sx={styles.pricingBadge}>
                <Typography sx={styles.pricingBadgeText}>{t("invitation.pricingBadge")}</Typography>
              </Box>

              <Typography sx={styles.pricingValue}>
                {t("invitation.pricingValue")}
                <Box component="span" sx={styles.pricingValueSuffix}>
                  {t("invitation.pricingValueSuffix")}
                </Box>
              </Typography>

              <Typography sx={styles.pricingSubtext}>{t("invitation.pricingSubtext")}</Typography>

              <Box sx={styles.pricingFeatureList}>
                {pricingFeatures.map((feature) => (
                  <Box key={feature} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box sx={styles.invitationCheckCircle}>
                      <CheckIcon sx={{ fontSize: 13, color: "#38bdf8" }} />
                    </Box>
                    <Typography sx={styles.pricingFeatureText}>{feature}</Typography>
                  </Box>
                ))}
              </Box>

              <Button
                component={Link}
                to={`${prefix}/signup`}
                fullWidth
                variant="contained"
                sx={styles.pricingCtaButton}
              >
                {t("invitation.ctaButton")}
              </Button>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

const SellersContent = () => {
  const { t } = useTranslation("static_sellers");

  const whyCards: WhyCardT[] = [
    {
      icon: <AttachMoneyIcon sx={{ fontSize: 36, color: "#38bdf8" }} />,
      title: t("whyJoin.cards.flexibleIncome.title"),
      description: t("whyJoin.cards.flexibleIncome.description"),
    },
    {
      icon: <LaptopIcon sx={{ fontSize: 36, color: "#38bdf8" }} />,
      title: t("whyJoin.cards.powerfulPlatform.title"),
      description: t("whyJoin.cards.powerfulPlatform.description"),
    },
    {
      icon: <SchoolIcon sx={{ fontSize: 36, color: "#38bdf8" }} />,
      title: t("whyJoin.cards.trainingSupport.title"),
      description: t("whyJoin.cards.trainingSupport.description"),
    },
    {
      icon: <TrendingUpIcon sx={{ fontSize: 36, color: "#38bdf8" }} />,
      title: t("whyJoin.cards.equityOwnership.title"),
      description: t("whyJoin.cards.equityOwnership.description"),
    },
  ];

  const steps: StepT[] = [
    {
      number: t("howItWorks.steps.step01.number"),
      title: t("howItWorks.steps.step01.title"),
      description: t("howItWorks.steps.step01.description"),
    },
    {
      number: t("howItWorks.steps.step02.number"),
      title: t("howItWorks.steps.step02.title"),
      description: t("howItWorks.steps.step02.description"),
    },
    {
      number: t("howItWorks.steps.step03.number"),
      title: t("howItWorks.steps.step03.title"),
      description: t("howItWorks.steps.step03.description"),
    },
  ];

  const testimonials: TestimonialT[] = [
    {
      name: t("testimonials.cards.joelGossett.name"),
      role: t("testimonials.cards.joelGossett.role"),
      location: t("testimonials.cards.joelGossett.location"),
      headline: t("testimonials.cards.joelGossett.headline"),
      quote: t("testimonials.cards.joelGossett.quote"),
      initials: "JG",
      avatarColor: "#1d4ed8",
      thumbnail: "/img/assets/testimonials/testimonial_JoelGossett.png",
    },
    {
      name: t("testimonials.cards.patrickAdu.name"),
      role: t("testimonials.cards.patrickAdu.role"),
      location: t("testimonials.cards.patrickAdu.location"),
      headline: t("testimonials.cards.patrickAdu.headline"),
      quote: t("testimonials.cards.patrickAdu.quote"),
      initials: "PA",
      avatarColor: "#0e7490",
      thumbnail: "/img/assets/Adu.jpg",
    },
    {
      name: t("testimonials.cards.jeffreyMartin.name"),
      role: t("testimonials.cards.jeffreyMartin.role"),
      location: t("testimonials.cards.jeffreyMartin.location"),
      headline: t("testimonials.cards.jeffreyMartin.headline"),
      quote: t("testimonials.cards.jeffreyMartin.quote"),
      initials: "JM",
      avatarColor: "#065f46",
      thumbnail: "/img/assets/jeff.jpg",
    },
    {
      name: t("testimonials.cards.binitaShah.name"),
      role: t("testimonials.cards.binitaShah.role"),
      location: t("testimonials.cards.binitaShah.location"),
      headline: t("testimonials.cards.binitaShah.headline"),
      quote: t("testimonials.cards.binitaShah.quote"),
      initials: "BS",
      avatarColor: "#7c3aed",
      thumbnail: "/img/assets/Binita.jpg",
    },
    {
      name: t("testimonials.cards.andrewDrasen.name"),
      role: t("testimonials.cards.andrewDrasen.role"),
      location: t("testimonials.cards.andrewDrasen.location"),
      headline: t("testimonials.cards.andrewDrasen.headline"),
      quote: t("testimonials.cards.andrewDrasen.quote"),
      initials: "AD",
      avatarColor: "#b45309",
      thumbnail: "/img/assets/testimonials/testimonial_AndrewDrasen.png",
    },
    {
      name: t("testimonials.cards.zacharyBach.name"),
      role: t("testimonials.cards.zacharyBach.role"),
      location: t("testimonials.cards.zacharyBach.location"),
      headline: t("testimonials.cards.zacharyBach.headline"),
      quote: t("testimonials.cards.zacharyBach.quote"),
      initials: "ZB",
      avatarColor: "#0f766e",
      thumbnail: "/img/assets/zach.jpg",
    },
    {
      name: t("testimonials.cards.jonathanBrunasso.name"),
      role: t("testimonials.cards.jonathanBrunasso.role"),
      location: t("testimonials.cards.jonathanBrunasso.location"),
      headline: t("testimonials.cards.jonathanBrunasso.headline"),
      quote: t("testimonials.cards.jonathanBrunasso.quote"),
      initials: "JB",
      avatarColor: "#1d4ed8",
      thumbnail: "/img/assets/JB3.jpg",
    },
    {
      name: t("testimonials.cards.tomCotter.name"),
      role: t("testimonials.cards.tomCotter.role"),
      location: t("testimonials.cards.tomCotter.location"),
      headline: t("testimonials.cards.tomCotter.headline"),
      quote: t("testimonials.cards.tomCotter.quote"),
      initials: "TC",
      avatarColor: "#0e7490",
      thumbnail: "/img/assets/testimonials/testimonial_TomCotter.png",
    },
  ];

  const statItems: StatItemT[] = [
    { target: 3677, label: t("stats.activeSellers"), delay: "0s" },
    { target: 282944329, prefix: "$", label: t("stats.commissionsPaid"), delay: "0.15s" },
    { target: 35000, suffix: "+", label: t("stats.homeInstallations"), delay: "0.3s" },
  ];

  const aiCards: AiCardT[] = [
    {
      icon: <AutoAwesomeIcon />,
      title: t("scaleImpact.cards.aiDesigns.title"),
      body: t("scaleImpact.cards.aiDesigns.body"),
    },
    {
      icon: <DescriptionIcon />,
      title: t("scaleImpact.cards.aiCustomerService.title"),
      body: t("scaleImpact.cards.aiCustomerService.body"),
    },
    {
      icon: <BoltIcon />,
      title: t("scaleImpact.cards.aiSalesEnablement.title"),
      body: t("scaleImpact.cards.aiSalesEnablement.body"),
    },
  ];

  const energyPanels: EnergyPanelT[] = [
    {
      stat: "3×",
      statFontSize: { xs: "5rem", md: "8rem" },
      statGradient: "linear-gradient(90deg, #3b82f6 0%, #38bdf8 55%, #67e8f9 100%)",
      statLabel: t("whyEnergy.panels.aiElectrification.statLabel"),
      statSubLabel: t("whyEnergy.panels.aiElectrification.statSubLabel"),
      headline: t("whyEnergy.panels.aiElectrification.headline"),
      body: t("whyEnergy.panels.aiElectrification.body"),
      statLeft: true,
    },
    {
      stat: "30%",
      statFontSize: { xs: "5rem", md: "8rem" },
      statGradient: "linear-gradient(90deg, #3b82f6 0%, #38bdf8 55%, #67e8f9 100%)",
      statLabel: t("whyEnergy.panels.energyBills.statLabel"),
      statSubLabel: t("whyEnergy.panels.energyBills.statSubLabel"),
      headline: t("whyEnergy.panels.energyBills.headline"),
      body: t("whyEnergy.panels.energyBills.body"),
      statLeft: false,
    },
    {
      stat: "$1T+",
      statFontSize: { xs: "5rem", md: "8rem" },
      statGradient: "linear-gradient(90deg, #3b82f6 0%, #38bdf8 55%, #67e8f9 100%)",
      statLabel: t("whyEnergy.panels.marketOpportunity.statLabel"),
      statSubLabel: t("whyEnergy.panels.marketOpportunity.statSubLabel"),
      headline: t("whyEnergy.panels.marketOpportunity.headline"),
      body: t("whyEnergy.panels.marketOpportunity.body"),
      statLeft: true,
    },
    {
      stat: "AI-PROOF",
      statFontSize: { xs: "3rem", md: "5rem" },
      statGradient: "linear-gradient(90deg, #3b82f6 0%, #38bdf8 55%, #67e8f9 100%)",
      statLabel: t("whyEnergy.panels.aiProof.statLabel"),
      statSubLabel: t("whyEnergy.panels.aiProof.statSubLabel"),
      headline: t("whyEnergy.panels.aiProof.headline"),
      body: t("whyEnergy.panels.aiProof.body"),
      statLeft: false,
    },
  ];

  const benefitCards: BenefitCardT[] = [
    {
      icon: <AttachMoneyIcon sx={{ fontSize: 32, color: "#38bdf8" }} />,
      title: t("whyPowur.benefitCards.topTierCommissions.title"),
      body: t("whyPowur.benefitCards.topTierCommissions.body"),
    },
    {
      icon: <BoltIcon sx={{ fontSize: 32, color: "#38bdf8" }} />,
      title: t("whyPowur.benefitCards.cashAdvance.title"),
      body: t("whyPowur.benefitCards.cashAdvance.body"),
    },
    {
      icon: <CheckCircleOutlineIcon sx={{ fontSize: 32, color: "#38bdf8" }} />,
      title: t("whyPowur.benefitCards.fullPayout.title"),
      body: t("whyPowur.benefitCards.fullPayout.body"),
    },
    {
      icon: <TrendingDownIcon sx={{ fontSize: 32, color: "#38bdf8" }} />,
      title: t("whyPowur.benefitCards.wholesalePricing.title"),
      body: t("whyPowur.benefitCards.wholesalePricing.body"),
    },
    {
      icon: <AccountBalanceIcon sx={{ fontSize: 32, color: "#38bdf8" }} />,
      title: t("whyPowur.benefitCards.innovativeFinancing.title"),
      body: t("whyPowur.benefitCards.innovativeFinancing.body"),
    },
    {
      icon: <DevicesIcon sx={{ fontSize: 32, color: "#38bdf8" }} />,
      title: t("whyPowur.benefitCards.proprietaryPlatform.title"),
      body: t("whyPowur.benefitCards.proprietaryPlatform.body"),
    },
    {
      icon: <PeopleIcon sx={{ fontSize: 32, color: "#38bdf8" }} />,
      title: t("whyPowur.benefitCards.scalableTeam.title"),
      body: t("whyPowur.benefitCards.scalableTeam.body"),
    },
    {
      icon: <StarIcon sx={{ fontSize: 32, color: "#38bdf8" }} />,
      title: t("whyPowur.benefitCards.stockOptions.title"),
      body: t("whyPowur.benefitCards.stockOptions.body"),
    },
    {
      icon: <BusinessIcon sx={{ fontSize: 32, color: "#38bdf8" }} />,
      title: t("whyPowur.benefitCards.enterpriseProgram.title"),
      body: t("whyPowur.benefitCards.enterpriseProgram.body"),
    },
  ];

  const rewardStatements = [
    t("reward.statements.noHiddenComplexity"),
    t("reward.statements.noOutdatedModels"),
    t("reward.statements.systemThatWorks"),
  ];

  const technologyFeatures: TechnologyFeatureT[] = [
    {
      title: t("technology.features.satelliteRoof.title"),
      description: t("technology.features.satelliteRoof.description"),
    },
    {
      title: t("technology.features.realtimeDesign.title"),
      description: t("technology.features.realtimeDesign.description"),
    },
    {
      title: t("technology.features.sideBySide.title"),
      description: t("technology.features.sideBySide.description"),
    },
    {
      title: t("technology.features.eSignature.title"),
      description: t("technology.features.eSignature.description"),
    },
  ];

  return (
    <Box sx={{ overflowX: "clip" }}>
      <HeroSection t={t} />
      <StatsSection statItems={statItems} />
      <WhyEnergyWhyPowurSection energyPanels={energyPanels} benefitCards={benefitCards} t={t} />
      <RewardSection rewardStatements={rewardStatements} t={t} />
      <ProvenAtScaleSection t={t} />
      <SmartPlatformSection t={t} />
      <WhyJoinSection whyCards={whyCards} t={t} />
      <ScaleYourImpactSection aiCards={aiCards} t={t} />
      <HowItWorksSection steps={steps} t={t} />
      <TechnologySection technologyFeatures={technologyFeatures} t={t} />
      <InvitationSection t={t} />
      <TestimonialsSection testimonials={testimonials} t={t} />
      <FinalCTASection t={t} />
    </Box>
  );
};

const SellersPage = () => {
  const { t } = useTranslation("static_sellers");
  return (
    <StaticPageLayout
      title={t("meta.title")}
      description={t("meta.description")}
      canonicalUrl="https://powur.com/join"
      ogTitle={t("meta.ogTitle")}
      ogDescription={t("meta.ogDescription")}
      noTopSpacer
    >
      <SellersContent />
    </StaticPageLayout>
  );
};

export default SellersPage;
